// pages/Schedule.js
import { useState, useEffect, useRef } from 'react';
import { ContentLayout, PageContainer } from '../components/layout';
import { Card, CardContent, Alert, Loading, ErrorMessage } from '../components/common';
import { useAppointmentsPG } from '../hooks/useAppointmentsPG';
import { useCustomers } from '../context/CustomerContext';
import { useServicesPG } from '../hooks/useServicesPG';
import { AppointmentModal } from '../components/modals/AppointmentModal';
import { useCrewMembersPG } from '../hooks/useCrewMembersPG';
import { EmptyTimeSlot } from '../components/schedule/AppointmentCard';
import { useTranslations } from '../hooks/useTranslations';
import { CrewMemberFilter } from '../components/schedule/CrewMemberFilter';
import { systemConfigService } from '../services/systemConfig';
import { Service } from '../models/Service';
import { useBusinessConfig } from '../context/BusinessConfigContext';
import { CalendarPlus, Calendar, Plus, Ban } from 'lucide-react';
import { HebrewCalendar, flags, Location } from '@hebcal/core';
import AppointmentPaymentBadge from '../components/schedule/AppointmentPaymentBadge';
import { useConstraints } from '../hooks/useConstraints';
import { ConstraintModal } from '../components/modals/ConstraintModal';

const getJewishHolidays = (year, month) => {
  const options = {
    year,
    month: month + 1,
    isHebrewYear: false,
    candlelighting: false,
    location: Location.lookup('Jerusalem'),
    il: true
  };

  const events = HebrewCalendar.calendar(options);
  const holidayMap = new Map();

  events.forEach(ev => {
    if (ev.getFlags() & flags.CHAG ||
      ev.getFlags() & flags.MINOR_HOLIDAY ||
      ev.getFlags() & flags.EREV) {
      const date = ev.getDate().greg();
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      holidayMap.set(key, ev.render('he'));
    }
  });

  return holidayMap;
};

export function Schedule({ onNavigate }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showSaturday, setShowSaturday] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState('');
  const [prefilledTime, setPrefilledTime] = useState('');
  const [prefilledCustomerId, setPrefilledCustomerId] = useState('');
  const [selectedCrewMember, setSelectedCrewMember] = useState('all');
  const [workingHours, setWorkingHours] = useState({
    startTime: '08:00',
    endTime: '20:30'
  });
  const [timeSlots, setTimeSlots] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('error');
  const [isMobile, setIsMobile] = useState(false);
  const [holidays, setHolidays] = useState(new Map());
  const [isCompactView, setIsCompactView] = useState(true);
  const [isWeeklyMobileView, setIsWeeklyMobileView] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempDateValue, setTempDateValue] = useState('');

  // Constraint modal state
  const [isConstraintModalOpen, setIsConstraintModalOpen] = useState(false);
  const [constraintModalMode, setConstraintModalMode] = useState('add');
  const [selectedConstraint, setSelectedConstraint] = useState(null);
  const [constraintModalInitialData, setConstraintModalInitialData] = useState(null);

  // Action picker: shown when clicking a weekly constraint on a specific date
  const [constraintActionPicker, setConstraintActionPicker] = useState(null); // { constraint, date }

  const datePickerRef = useRef(null);

  const {
    activeCrewMembers,
    loading: crewMembersLoading,
    error: crewMembersError,
    clearError: clearCrewMembersError
  } = useCrewMembersPG();

  const { settings } = useBusinessConfig();

  const t = useTranslations();

  const {
    constraints,
    loadConstraints,
    createConstraint,
    updateConstraint,
    deleteConstraint,
    excludeDate
  } = useConstraints();

  // Use Firestore hooks instead of hardcoded data
  const {
    appointments,
    loading: appointmentsLoading,
    error: appointmentsError,
    handleAppointmentSubmit,
    clearError: clearAppointmentsError,
    loadAppointmentsByDateRange
  } = useAppointmentsPG();

  const {
    loading: customersLoading,
    error: customersError,
    clearError: clearCustomersError
  } = useCustomers();

  const {
    services,
    loading: servicesLoading,
    error: servicesError,
    clearError: clearServicesError
  } = useServicesPG();

  // Combined loading and error states
  console.log("Loading status: ", { appointmentsLoading, customersLoading, servicesLoading, crewMembersLoading });
  const hasError = appointmentsError || customersError || servicesError || crewMembersError;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Update current time every minute
  useEffect(() => {
    const updateCurrentTime = () => {
      setCurrentTime(new Date());
    };

    // Update immediately
    updateCurrentTime();

    // Update every minute
    const interval = setInterval(updateCurrentTime, 60000);

    return () => clearInterval(interval);
  }, []);

  const clearAllErrors = () => {
    clearAppointmentsError();
    clearCustomersError();
    clearServicesError();
    clearCrewMembersError();
  };

  const getServiceForAppointment = (appointment) => {
    return services.find(service => service.id === appointment.service_id) ||
      services.find(service => service.name === appointment.service_name) ||
      { color: '#F8BBD9' }; // Default service color
  };

  const toDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Returns constraints that overlap a given 30-minute slot
  const getConstraintBlocksForSlot = (date, slotTime) => {
    const timeToMin = (t) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const slotStart = timeToMin(slotTime);
    const slotEnd = slotStart + 30;
    const dayOfWeek = new Date(date + 'T12:00:00').getDay(); // 0=Sun

    return constraints.filter(c => {
      // Crew member scope: show if global (null) or matches current filter
      if (c.crew_member_id && selectedCrewMember !== 'all' && c.crew_member_id !== selectedCrewMember) {
        return false;
      }
      if (c.constraint_type === 'weekly') {
        if (!c.days_of_week || !c.days_of_week.includes(dayOfWeek)) return false;
        if (c.excluded_dates && c.excluded_dates.includes(date)) return false;
        if (!c.start_time || !c.end_time) return false;
        const cStart = timeToMin(c.start_time);
        const cEnd = timeToMin(c.end_time);
        return slotStart < cEnd && slotEnd > cStart;
      }
      if (c.constraint_type === 'date_range') {
        if (!c.start_date || !c.end_date) return false;
        if (date < c.start_date || date > c.end_date) return false;
        if (c.all_day) return true;
        if (!c.start_time || !c.end_time) return false;
        const cStart = timeToMin(c.start_time);
        const cEnd = timeToMin(c.end_time);
        return slotStart < cEnd && slotEnd > cStart;
      }
      return false;
    });
  };

  const handleCustomerUpdated = async () => {
    await loadAppointmentsByDateRange(scheduleLoadStartDate, scheduleLoadEndDate, true);
  };

  const handleAppointmentSubmitAndReload = async (...args) => {
    const result = await handleAppointmentSubmit(...args);
    const mode = args[1]
    if (result.success) {
      await loadAppointmentsByDateRange(scheduleLoadStartDate, scheduleLoadEndDate, true);
    }

    switch (mode) {
      case 'add':
      case 'edit':
        setAlertVariant('success');
        setAlertMessage(args[1] === 'edit' ? 'הפגישה עודכנה בהצלחה' : 'הפגישה נוצרה בהצלחה');
        break;
      case 'delete':
        setAlertVariant('warning');
        setAlertMessage('הפגישה נמחקה בהצלחה');
        break;
      default:
        break;
    }

    setTimeout(() => {
      setAlertMessage('');
    }, 2500);

    return result;
  };

  const getPrimaryAppointmentsForSlot = (date, time) => {
    const filteredAppointments = getFilteredAppointments();
    const slotIndex = timeSlots.indexOf(time);

    return filteredAppointments.filter(apt => {
      if (apt.date !== date || apt.status === 'cancelled') return false;
      const { slotIndex: aptSlotIndex } = findContainingSlot(apt.time);
      return aptSlotIndex === slotIndex;
    });
  };

  const getStackId = (date, time) => `${date}-${time}`;

  const getStackPosition = (appointment, allAppointments, stackId) => {
    // Simply return the index position - no dynamic reordering
    return allAppointments.findIndex(apt => apt.id === appointment.id);
  };

  const getFilteredAppointments = () => {
    if (selectedCrewMember === 'all') {
      return appointments;
    }
    return appointments.filter(apt => apt.crew_member_id === selectedCrewMember);
  };

  // Get the start of the week (Sunday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  // Get the end of the week (Saturday)
  const getWeekEnd = (date) => {
    const d = getWeekStart(date);
    d.setDate(d.getDate() + 6);
    return d;
  };

  // Get current week range
  const weekStart = getWeekStart(currentDate);
  const weekEnd = getWeekEnd(currentDate);

  const mobileVisibleEnd = new Date(currentDate);
  mobileVisibleEnd.setDate(mobileVisibleEnd.getDate() + 1);

  const scheduleLoadStart = isMobile && !isWeeklyMobileView ? getWeekStart(currentDate) : weekStart;
  const scheduleLoadEnd = isMobile && !isWeeklyMobileView ? getWeekEnd(mobileVisibleEnd) : weekEnd;
  const scheduleLoadStartDate = toDateKey(scheduleLoadStart);
  const scheduleLoadEndDate = toDateKey(scheduleLoadEnd);
  const scheduleLoadStartMonthKey = `${scheduleLoadStart.getFullYear()}-${scheduleLoadStart.getMonth()}`;
  const scheduleLoadEndMonthKey = `${scheduleLoadEnd.getFullYear()}-${scheduleLoadEnd.getMonth()}`;

  const formatWeekRange = () => {
    if (isMobile && !isWeeklyMobileView) {
      // Two days format for mobile
      const endDate = new Date(currentDate);
      endDate.setDate(endDate.getDate() + 1);
      const options = { month: 'short', day: 'numeric' };
      const start = currentDate.toLocaleDateString('he-IL', options);
      const end = endDate.toLocaleDateString('he-IL', options);
      return `${start} - ${end}`;
    }
    // Week range for desktop or mobile weekly view
    const options = { month: 'short', day: 'numeric' };
    const start = weekStart.toLocaleDateString('he-IL', options);
    const end = weekEnd.toLocaleDateString('he-IL', options);
    const year = weekStart.getFullYear();
    return `${start} - ${end}, ${year}`;
  };

  useEffect(() => {
    if (settings && settings.workingHours) {
      setWorkingHours(settings.workingHours);
      const slots = systemConfigService.generateTimeSlots(settings.workingHours.startTime, settings.workingHours.endTime);
      setTimeSlots(slots);
    }
  }, [settings]);

  useEffect(() => {
    const loadWeekAppointments = async () => {
      console.log('Schedule: Loading week appointments', {
        weekStart: scheduleLoadStartDate,
        weekEnd: scheduleLoadEndDate,
        context: 'Schedule component mounted/week changed'
      });

      try {
        await loadAppointmentsByDateRange(scheduleLoadStartDate, scheduleLoadEndDate);
        await loadConstraints({ startDate: scheduleLoadStartDate, endDate: scheduleLoadEndDate });
      } catch (error) {
        console.error('Error loading week appointments:', error);
      }
    };

    loadWeekAppointments();
  }, [scheduleLoadStartDate, scheduleLoadEndDate, loadAppointmentsByDateRange, loadConstraints]);

  useEffect(() => {
    const [startYear, startMonth] = scheduleLoadStartMonthKey.split('-').map(Number);
    const [endYear, endMonth] = scheduleLoadEndMonthKey.split('-').map(Number);
    const holidayMap = getJewishHolidays(startYear, startMonth);
    if (scheduleLoadEndMonthKey !== scheduleLoadStartMonthKey) {
      const nextMap = getJewishHolidays(endYear, endMonth);
      nextMap.forEach((value, key) => holidayMap.set(key, value));
    }
    setHolidays(holidayMap);
  }, [scheduleLoadStartMonthKey, scheduleLoadEndMonthKey]);

  // Get days of the week
  const getDaysOfWeek = () => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Mobile: show 2 days by default, or full week if weekly view is enabled
    if (isMobile && !isWeeklyMobileView) {
      // Show 2 days on mobile
      for (let i = 0; i < 2; i++) {
        const day = new Date(currentDate);
        day.setDate(day.getDate() + i);

        const dayString = day.getFullYear() + '-' +
          String(day.getMonth() + 1).padStart(2, '0') + '-' +
          String(day.getDate()).padStart(2, '0');

        const todayString = today.getFullYear() + '-' +
          String(today.getMonth() + 1).padStart(2, '0') + '-' +
          String(today.getDate()).padStart(2, '0');

        days.push({
          name: day.toLocaleDateString('he-IL', { weekday: 'short' }),
          date: day.getDate().toString(),
          fullDate: dayString,
          fullName: day.toLocaleDateString('he-IL', { weekday: 'long' }),
          isToday: dayString === todayString
        });
      }
      return days;
    }

    for (let i = 0; i < (showSaturday ? 7 : 6); i++) {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);

      const today = new Date();
      const todayString = today.getFullYear() + '-' +
        String(today.getMonth() + 1).padStart(2, '0') + '-' +
        String(today.getDate()).padStart(2, '0');

      const dayString = day.getFullYear() + '-' +
        String(day.getMonth() + 1).padStart(2, '0') + '-' +
        String(day.getDate()).padStart(2, '0');

      const isToday = dayString === todayString;

      days.push({
        name: day.toLocaleDateString('he-IL', { weekday: 'short' }),
        date: day.getDate().toString(),
        fullDate: dayString,
        fullName: day.toLocaleDateString('he-IL', { weekday: 'long' }),
        isToday
      });
    }
    return days;
  };

  const days = getDaysOfWeek();

  // Helper: Convert time string to minutes since midnight
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper: Calculate current time line position (5-minute resolution)
  const getCurrentTimePosition = () => {
    const now = currentTime;
    const hours = now.getHours();
    const minutes = now.getMinutes();

    // Round to nearest 5 minutes for 5-minute resolution
    const roundedMinutes = Math.round(minutes / 5) * 5;
    const currentMinutes = hours * 60 + roundedMinutes;

    const startMinutes = timeToMinutes(workingHours.startTime);
    const endMinutes = timeToMinutes(workingHours.endTime);

    // Check if current time is within working hours
    if (currentMinutes < startMinutes || currentMinutes > endMinutes) {
      return null;
    }

    // Calculate position as percentage within the schedule
    const totalMinutes = endMinutes - startMinutes;
    const minutesFromStart = currentMinutes - startMinutes;
    const positionPercent = (minutesFromStart / totalMinutes) * 100;

    return positionPercent;
  };

  // Helper: Find the 30-minute slot that contains or precedes a given time
  const findContainingSlot = (appointmentTime) => {
    const aptMinutes = timeToMinutes(appointmentTime);

    // Find the slot index where this appointment should appear
    for (let i = 0; i < timeSlots.length; i++) {
      const slotMinutes = timeToMinutes(timeSlots[i]);
      const nextSlotMinutes = i < timeSlots.length - 1 ? timeToMinutes(timeSlots[i + 1]) : slotMinutes + 30;

      // If appointment time falls within this slot
      if (aptMinutes >= slotMinutes && aptMinutes < nextSlotMinutes) {
        return {
          slotIndex: i,
          offsetMinutes: aptMinutes - slotMinutes
        };
      }
    }

    // Fallback: return last slot if time is after all slots
    return {
      slotIndex: timeSlots.length - 1,
      offsetMinutes: 0
    };
  };

  // Calculate slots occupied by appointment
  const getAppointmentSlots = (appointment) => {
    const slotsNeeded = Math.ceil(appointment.duration / 30);
    const { slotIndex } = findContainingSlot(appointment.time);
    return {
      startIndex: slotIndex,
      slotsNeeded,
      endIndex: slotIndex + slotsNeeded - 1
    };
  };

  // Get appointments for a specific day and time (primary slot only)
  const getAppointmentForSlot = (date, time) => {
    const filteredAppointments = getFilteredAppointments();
    return filteredAppointments.find(apt => {
      return (apt.date === date &&
        apt.time === time &&
        apt.status !== 'cancelled')
    }
    );
  };

  // Check if a slot is occupied by any appointment (including secondary slots)
  const isSlotOccupied = (date, time) => {
    const currentTimeIndex = timeSlots.indexOf(time);
    const filteredAppointments = getFilteredAppointments();

    return filteredAppointments.some(apt => {
      if (apt.status === 'cancelled') return false;
      if (apt.date !== date) return false;
      const { startIndex, endIndex } = getAppointmentSlots(apt);
      return currentTimeIndex >= startIndex && currentTimeIndex <= endIndex;
    });
  };

  const getCrewMemberInfo = (crewMemberId) => {
    return activeCrewMembers.find(cm => cm.id === crewMemberId);
  };

  // Check if slot is a secondary slot (not the primary appointment slot)
  const isSecondarySlot = (date, time) => {
    const currentTimeIndex = timeSlots.indexOf(time);
    return appointments.some(apt => {
      if (apt.date !== date || apt.status === 'cancelled') return false;
      const { startIndex, endIndex } = getAppointmentSlots(apt);
      return currentTimeIndex > startIndex && currentTimeIndex <= endIndex;
    });
  };

  // Get the appointment that occupies a slot (including secondary slots)
  const getAppointmentOccupyingSlot = (date, time) => {
    const currentTimeIndex = timeSlots.indexOf(time);
    const filteredAppointments = getFilteredAppointments();

    return filteredAppointments.find(apt => {
      if (apt.status === 'cancelled') return false;
      if (apt.date !== date) return false;
      const { startIndex, endIndex } = getAppointmentSlots(apt);
      return currentTimeIndex >= startIndex && currentTimeIndex <= endIndex;
    });
  };

  const handlePreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - (isMobile && !isWeeklyMobileView ? 2 : 7));
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (isMobile && !isWeeklyMobileView ? 2 : 7));
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateChange = (e) => {
    setTempDateValue(e.target.value);
  };

  const handleDateConfirm = () => {
    if (!tempDateValue) {
      setShowDatePicker(false);
      return;
    }

    // Validate the date
    const selectedDate = new Date(tempDateValue + 'T12:00:00');

    // Check if the date is valid
    if (isNaN(selectedDate.getTime())) {
      // Invalid date - ignore and keep previous value
      setTempDateValue('');
      setShowDatePicker(false);
      return;
    }

    // Check if date components are valid
    const [year, month, day] = tempDateValue.split('-').map(Number);
    if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
      // Invalid date components - ignore and keep previous value
      setTempDateValue('');
      setShowDatePicker(false);
      return;
    }

    // Date is valid - update current date
    setCurrentDate(selectedDate);
    setTempDateValue('');
    setShowDatePicker(false);
  };

  const handleDatePickerOpen = () => {
    setTempDateValue(currentDate.toISOString().split('T')[0]);
    setShowDatePicker(true);
  };

  const handleDateKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleDateConfirm();
    } else if (e.key === 'Escape') {
      setShowDatePicker(false);
    }
  };

  const handleNewAppointment = (date = '', time = '') => {
    setModalMode('add');
    setSelectedAppointment(null);
    setPrefilledDate(date);
    setPrefilledTime(time);
    setIsModalOpen(true);
  };

  const handleEditAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setModalMode('edit');
    setPrefilledDate('');
    setPrefilledTime('');
    setPrefilledCustomerId(appointment.customer_id)
    setIsModalOpen(true);
  };

  const handleSlotClick = (date, time) => {
    if (selectedCrewMember !== 'all') {
      // Pre-fill the crew member if one is selected in the filter
      handleNewAppointment(date, time, selectedCrewMember);
    } else {
      handleNewAppointment(date, time);
    }
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  if (timeSlots.length === 0 || appointmentsLoading || customersLoading || servicesLoading || crewMembersLoading) {
    return (
      <ContentLayout
        currentPage="schedule"
        onNavigate={onNavigate}
        title={t.scheduleManagement}
        description={t.manageAppointmentBookings}
        primaryAction={{
          label: t.newAppointment,
          onClick: () => handleNewAppointment(),
          icon: <CalendarPlus className="w-4 h-4" />
        }}
      >
        <PageContainer>
          <div className="flex justify-center items-center h-64">
            <Loading message={t.loadingScheduleData} />
          </div>
        </PageContainer>
      </ContentLayout>
    );
  }

  // Create the view toggle component for the header (mobile only)
  const ViewToggle = isMobile ? (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
      <button
        onClick={() => setIsWeeklyMobileView(false)}
        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${!isWeeklyMobileView
            ? 'bg-pink-500 text-white shadow-sm'
            : 'text-gray-700 hover:text-gray-900'
          }`}
      >
        יומיים
      </button>
      <button
        onClick={() => setIsWeeklyMobileView(true)}
        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isWeeklyMobileView
            ? 'bg-pink-500 text-white shadow-sm'
            : 'text-gray-700 hover:text-gray-900'
          }`}
      >
        שבוע
      </button>
    </div>
  ) : null;

  return (
    <ContentLayout
      currentPage="schedule"
      onNavigate={onNavigate}
      title={t.scheduleManagement}
      description={t.manageAppointmentBookings}
      primaryAction={{
        label: t.newAppointment,
        onClick: () => handleNewAppointment(),
        icon: <CalendarPlus className="w-4 h-4" />
      }}
      secondaryActions={[
        {
          label: 'הגבלת זמן חדשה',
          onClick: () => {
            setConstraintModalMode('add');
            setSelectedConstraint(null);
            setIsConstraintModalOpen(true);
          },
          icon: <Ban className="w-4 h-4" />
        }
      ]}
      centerHeaderComponent={ViewToggle}
    >
      <PageContainer
        className="lg:p-4 flex flex-col h-[calc(100vh-60px)] lg:h-auto"
        mx='mx-0'
        px='px-0 sm:px-6 lg:px-8'>
        {/* Error handling */}
        {hasError && (
          <ErrorMessage
            message={[appointmentsError, customersError, servicesError].filter(Boolean)}
            dismissible
            onDismiss={clearAllErrors}
            className="mb-4"
          />
        )}

        {/* Schedule Controls (mobile-first) */}
        <div className="flex-shrink-0 lg:mb-3 sm:mb-6 bg-white rounded-md sm:rounded-lg border border-gray-200 p-1 sm:p-4">
          <div className="flex flex-col gap-3">
            {/* Crew Member Filter - Full width on top */}
            <div className="w-full border-b border-gray-200">
              <CrewMemberFilter
                selectedCrewMember={selectedCrewMember}
                onCrewMemberChange={setSelectedCrewMember}
                crewMembers={activeCrewMembers}
                appointments={getFilteredAppointments()}
              />
            </div>

            {/* Date Navigation and Controls - Second row */}
            <div className="flex items-center gap-2 sm:items-center justify-between rtl:sm:flex-row-reverse">

              {/* Compact week nav + date */}
              <div className="flex items-center gap-2">
                {/* Today Button */}
                <button
                  onClick={handleToday}
                  className="px-3 py-1.5 mx-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  title="חזור להיום"
                >
                  היום
                </button>

                {/* Prev */}
                <button
                  onClick={handlePreviousWeek}
                  aria-label="Previous week"
                  className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-700 rtl:hidden" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <svg className="w-4 h-4 text-gray-700 hidden rtl:block" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Date range (tiny on mobile) - clickable to show date picker */}
                <div className="min-w-0 flex items-center justify-center gap-2 relative" ref={datePickerRef}>
                  {/* Hide calendar icon on mobile to save vertical space */}
                  <button
                    onClick={handleDatePickerOpen}
                    className="hidden sm:flex items-center justify-center w-5 h-5 shrink-0 text-red-500 hover:text-red-600 transition-colors"
                    aria-label="Select date"
                    title="בחר תאריך"
                  >
                    <Calendar className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleDatePickerOpen}
                    className="text-sm leading-4 sm:text-lg sm:leading-6 font-semibold text-gray-900 truncate hover:text-blue-600 transition-colors cursor-pointer"
                    title="לחץ לבחירת תאריך"
                  >
                    {formatWeekRange()}
                  </button>

                  {/* Date picker dropdown */}
                  {showDatePicker && (
                    <div className="absolute top-full mt-2 z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">בחר תאריך:</label>
                        <input
                          type="date"
                          onChange={handleDateChange}
                          onKeyDown={handleDateKeyDown}
                          value={tempDateValue}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleDateConfirm}
                            className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-medium text-sm"
                          >
                            אישור
                          </button>
                          <button
                            onClick={() => setShowDatePicker(false)}
                            className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium text-sm"
                          >
                            ביטול
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Next */}
                <button
                  onClick={handleNextWeek}
                  aria-label="Next week"
                  className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-700 rtl:hidden" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <svg className="w-4 h-4 text-gray-700 hidden rtl:block" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              {/* Compact View Toggle */}
              <button
                onClick={() => setIsCompactView(!isCompactView)}
                className="w-7 h-7 mx-2 sm:w-8 sm:h-8 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                title={isCompactView ? 'תצוגה רגילה' : 'תצוגה מצומצמת'}
              >
                {isCompactView ? (
                  <svg className="w-4 h-4 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 6a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 6a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        <Card padding='none' className="flex-1 lg:flex-none min-h-0 overflow-hidden">
          <CardContent className="p-0 h-full">
            <div className="h-full lg:h-[600px] overflow-y-auto overflow-x-hidden rounded-lg border border-gray-200">
              <div className="grid grid-cols-[var(--time-w),1fr] relative
                   [--time-w:56px] sm:[--time-w:80px]">

                {/* ===== Left: Fixed time column (no horizontal scroll) ===== */}
                <div className="z-20 bg-gray-50 border-e border-gray-200 w-[var(--time-w)]">
                  {/* Only vertical stickiness for its header */}
                  {/* Sticky header cell */}
                  <div className="sticky top-0 z-30 h-12 flex items-center justify-center text-xs sm:text-sm font-semibold text-gray-900 border-b border-gray-200 bg-gray-50">
                    שעה
                  </div>

                  {/* Time labels */}
                  {timeSlots.map((time) => (
                    <div
                      key={`time-${time}`}
                      className={`${isCompactView ? 'h-6' : 'h-12'} px-2 sm:px-3 flex items-center text-xs sm:text-sm font-medium text-gray-700 border-b border-gray-100 bg-gray-50 transition-all duration-200`}
                    >
                      {formatTime(time)}
                    </div>
                  ))}
                </div>

                {/* ===== Right: Days area ===== */}
                <div
                  className="relative overflow-y-visible
             [--col-w:calc(100%/2)]
             xs:[--col-w:calc(100%/3)]
             sm:[--col-w:auto]"
                >
                  {/* Sticky days header */}
                  <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-200">
                    <div className={isWeeklyMobileView ? '' : 'overflow-x-auto'}>
                      <div
                        className={[
                          isMobile && !isWeeklyMobileView ? 'grid grid-cols-2' : 'grid',
                          showSaturday ? 'sm:grid-cols-7' : 'sm:grid-cols-6',
                          isWeeklyMobileView ? 'grid-cols-7' : 'grid-flow-col auto-cols-[var(--col-w)]'
                        ].join(' ')}
                      >
                        {days.map((day) => {
                          const holidayName = holidays.get(day.fullDate);

                          return (
                            <div
                              key={`hdr-${day.fullDate}`}
                              className={[
                                'h-12 flex flex-col items-center justify-center text-center relative',
                                isWeeklyMobileView ? 'px-0.5' : 'px-2 min-w-[var(--col-w)] sm:min-w-0',
                                day.isToday ? 'bg-pink-50' : 'bg-gray-50'
                              ].join(' ')}
                            >
                              <div className={[
                                'font-medium text-gray-900 leading-tight',
                                isWeeklyMobileView ? 'text-[9px]' : 'text-[11px] sm:text-xs'
                              ].join(' ')}>
                                {day.name} {holidayName && !isWeeklyMobileView && holidayName}
                              </div>
                              <div className={[
                                'font-bold leading-tight',
                                isWeeklyMobileView ? 'text-sm' : 'text-base sm:text-xl',
                                day.isToday ? 'text-blue-600' : 'text-gray-700'
                              ].join(' ')}>
                                {day.date}
                              </div>
                              {holidayName && (
                                <div
                                  className="absolute top-1 right-1 w-2 h-2 bg-purple-500 rounded-full"
                                  title={holidayName}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {/* Time rows */}
                  <div className="relative">
                    {timeSlots.map((time) => (
                      <div
                        key={`row-${time}`}
                        className={[
                          isMobile && !isWeeklyMobileView ? 'grid grid-cols-2' : 'grid grid-flow-col auto-cols-[var(--col-w)]',
                          showSaturday ? 'sm:grid-cols-7' : 'sm:grid-cols-6',
                          'sm:auto-cols-auto sm:grid-flow-row',
                          isWeeklyMobileView ? 'grid-cols-7' : ''
                        ].join(' ')}
                      >
                        {days.map((day) => {
                          const primaryAppointments = getPrimaryAppointmentsForSlot(day.fullDate, time);
                          const isOccupied = isSlotOccupied(day.fullDate, time);
                          const occupyingAppointment = getAppointmentOccupyingSlot(day.fullDate, time);
                          const stackId = getStackId(day.fullDate, time);
                          const holidayName = holidays.get(day.fullDate);
                          const isSecondary = isSecondarySlot(day.fullDate, time);

                          const constraintBlocks = getConstraintBlocksForSlot(day.fullDate, time);

                          return (
                            <div
                              key={`${day.fullDate}-${time}`}
                              className={`relative z-0 ${isCompactView ? 'h-6' : 'h-12'} border-e ${isSecondary ? '' : 'border-b'} border-gray-100 sm:last:border-e-0 transition-all duration-200`}>
                              {/* Constraint blocks — rendered as a background layer */}
                              {constraintBlocks.map(constraint => {
                                const timeToMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
                                const slotStart = timeToMin(time);
                                const slotHeightRem = isCompactView ? 1.5 : 3;
                                const cStart = constraint.all_day ? slotStart : timeToMin(constraint.start_time);
                                const cEnd = constraint.all_day ? slotStart + 30 : timeToMin(constraint.end_time);
                                const visStart = Math.max(slotStart, cStart);
                                const visEnd = Math.min(slotStart + 30, cEnd);
                                const topPct = ((visStart - slotStart) / 30) * 100;
                                const heightRem = ((visEnd - visStart) / 30) * slotHeightRem;
                                return (
                                  <div
                                    key={`constraint-${constraint.id}`}
                                    className="absolute inset-x-0 z-10 overflow-hidden cursor-pointer"
                                    style={{
                                      top: `${topPct}%`,
                                      height: `${heightRem}rem`,
                                      backgroundColor: '#6B7280',
                                      opacity: 0.75,
                                      borderRight: '3px solid #374151'
                                    }}
                                    onClick={() => {
                                      if (constraint.constraint_type === 'weekly') {
                                        setConstraintActionPicker({ constraint, date: day.fullDate });
                                      } else {
                                        setSelectedConstraint(constraint);
                                        setConstraintModalMode('edit');
                                        setConstraintModalInitialData(null);
                                        setIsConstraintModalOpen(true);
                                      }
                                    }}
                                    title={constraint.reason}
                                  >
                                    <div className="px-1 py-0.5 text-white text-[9px] font-medium truncate">
                                      {constraint.reason}
                                    </div>
                                  </div>
                                );
                              })}
                              {primaryAppointments.length > 0 ? (
                                primaryAppointments.map((appointment) => {
                                  const stackPosition = getStackPosition(appointment, primaryAppointments, stackId);
                                  const isTopCard = stackPosition === primaryAppointments.length - 1;
                                  const offset = 8;

                                  // Calculate precise positioning for 10-minute intervals
                                  const { offsetMinutes } = findContainingSlot(appointment.time);
                                  const slotHeightRem = isCompactView ? 1.5 : 3;
                                  // Calculate height based on actual duration (more precise than 30-min slots)
                                  const heightRem = (appointment.duration / 30) * slotHeightRem;

                                  // Calculate top offset: proportion of slot height based on minutes offset
                                  // offsetMinutes is 0-29, representing position within the 30-minute slot
                                  const topOffsetPercent = (offsetMinutes / 30) * 100;

                                  // Helper function to darken a color for the border
                                  const darkenColor = (color, amount = 0.1) => {
                                    const hex = color.replace('#', '');
                                    const r = parseInt(hex.substring(0, 2), 16);
                                    const g = parseInt(hex.substring(2, 4), 16);
                                    const b = parseInt(hex.substring(4, 6), 16);

                                    const newR = Math.max(0, Math.floor(r * (1 - amount)));
                                    const newG = Math.max(0, Math.floor(g * (1 - amount)));
                                    const newB = Math.max(0, Math.floor(b * (1 - amount)));

                                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                                  };

                                  const cardColor = appointment.status === 'pending_approval'
                                    ? '#cda711ff'
                                    : getServiceForAppointment(appointment)?.color || '#F8BBD9';
                                  const borderColor = darkenColor(cardColor);

                                  return (
                                    <div
                                      key={appointment.id}
                                      className={[
                                        'absolute p-1 rounded text-[10px] sm:text-xs text-white cursor-pointer overflow-hidden',
                                        'transition-all duration-200 appointment-card border-2',
                                        appointment.status === 'cancelled' ? 'opacity-50' : '',
                                        isTopCard ? 'shadow-lg' : 'shadow-md'
                                      ].join(' ')}
                                      style={{
                                        backgroundColor: cardColor,
                                        borderColor: borderColor,
                                        height: `${heightRem}rem`,
                                        top: `calc(${topOffsetPercent}% + ${stackPosition * offset}px)`,
                                        left: `${stackPosition * offset}px`,
                                        right: `${stackPosition * offset}px`,
                                        bottom: 'auto',
                                        zIndex: 20 + stackPosition,
                                        transform: isTopCard ? 'scale(1)' : 'scale(0.98)',
                                        filter: isTopCard ? 'brightness(1)' : 'brightness(0.9)'
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setTimeout(() => handleEditAppointment(appointment), 150);
                                      }}
                                      title={`${appointment.customer_name} - ${appointment.service_name}\n${formatTime(appointment.time)} - ${appointment.getEndTime()}\nCrew: ${appointment.crew_member_name || 'Unassigned'}\nStatus: ${appointment.status}\nClick to edit`}
                                    >
                                      <div className="flex items-start justify-between h-full">
                                        {isMobile && isWeeklyMobileView ? (
                                          <div className="flex-1 min-w-0">
                                            <div className="font-medium break-words leading-tight">{appointment.customer_name}</div>
                                          </div>
                                        ) : (
                                          <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">{appointment.customer_name}</div>
                                            {!isCompactView && (<div className="opacity-90 truncate">{appointment.service_name}</div>)}
                                            {!isCompactView && (<div className="opacity-75 truncate">
                                              {formatTime(appointment.getEndTime())} - {appointment.getFormattedTime()}
                                            </div>)}
                                            {appointment.notes && !isCompactView &&
                                              Service.getServicesWithNotesOnCard().includes(appointment.service_name) && (
                                                <div className="opacity-85 truncate font-medium">{appointment.notes}</div>
                                              )}
                                          </div>
                                        )}

                                        {/* Payment Status Badge */}
                                        <AppointmentPaymentBadge
                                          appointment={appointment}
                                          isMobile={isMobile}
                                          isWeeklyMobileView={isWeeklyMobileView}
                                        />

                                        {!(isMobile && isWeeklyMobileView) && (
                                          <div
                                            className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-xs font-medium ms-1"
                                            style={{
                                              backgroundColor: getCrewMemberInfo(appointment.crew_member_id)?.color || '#6B7280',
                                              color: 'white'
                                            }}
                                            title={appointment.crew_member_name || 'Unassigned'}
                                          >
                                            {appointment.getCrewMemberInitials() || '?'}
                                          </div>
                                        )}

                                        {!(isMobile && isWeeklyMobileView) && appointment.status === 'pending_approval' && (
                                          <div className="absolute bottom-1 start-1">
                                            <div className="bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full text-[10px] font-medium border border-amber-300">
                                              ממתין
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {!(isMobile && isWeeklyMobileView) && primaryAppointments.length > 1 && (
                                        <div className="absolute top-1 end-1">
                                          <div className="w-4 h-4 bg-white/30 rounded-full flex items-center justify-center text-[10px] font-bold">
                                            {primaryAppointments.length}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                              ) : !isOccupied ? (
                                <EmptyTimeSlot
                                  date={day.fullDate}
                                  time={time}
                                  selectedCrewMember={selectedCrewMember}
                                  activeCrewMembers={activeCrewMembers}
                                  onClick={() => handleSlotClick(day.fullDate, time)}
                                  className={holidayName ? 'bg-green-100' : day.isToday ? 'bg-pink-100' : ''}
                                />
                              ) : occupyingAppointment ? (
                                // Render clickable overlay for secondary slots
                                <div
                                  className="absolute inset-0 cursor-pointer appointment-card-overlay"
                                  style={{
                                    backgroundColor: occupyingAppointment.status === 'pending_approval'
                                      ? '#cda711ff'
                                      : getServiceForAppointment(occupyingAppointment)?.color || '#F8BBD9',
                                    opacity: 0.3
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTimeout(() => handleEditAppointment(occupyingAppointment), 150);
                                  }}
                                  title={`${occupyingAppointment.customer_name} - ${occupyingAppointment.service_name}\n${formatTime(occupyingAppointment.time)} - ${occupyingAppointment.getEndTime()}\nCrew: ${occupyingAppointment.crew_member_name || 'Unassigned'}\nStatus: ${occupyingAppointment.status}\nClick to edit`}
                                />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ))}

                    {/* Current time indicator line - only show on today's column */}
                    {(() => {
                      const currentTimePosition = getCurrentTimePosition();
                      if (currentTimePosition === null) return null;

                      // Get current date string for accurate comparison
                      const now = new Date();
                      const todayString = now.getFullYear() + '-' +
                        String(now.getMonth() + 1).padStart(2, '0') + '-' +
                        String(now.getDate()).padStart(2, '0');

                      // Find today's column index by comparing fullDate strings
                      const todayColumnIndex = days.findIndex(day => day.fullDate === todayString);

                      if (todayColumnIndex === -1) return null;

                      // Calculate column width and position
                      const numberOfColumns = days.length;
                      const columnWidthPercent = 100 / numberOfColumns;
                      // For RTL layout, we need to position from the right edge
                      // Flip the index: rightmost column (index 0) should be at right: 0%
                      const rightPositionPercent = todayColumnIndex * columnWidthPercent;

                      return (
                        <div
                          className="absolute pointer-events-none z-40"
                          style={{
                            top: `${currentTimePosition}%`,
                            right: `${rightPositionPercent}%`,
                            width: `${columnWidthPercent}%`
                          }}
                        >
                          <div className="relative w-full">
                            {/* Red line */}
                            <div className="absolute w-full h-0.5 bg-red-500 shadow-md"></div>
                            {/* Optional: Red circle at the start of the line */}
                            <div className="absolute -left-1 -top-1 w-2 h-2 bg-red-500 rounded-full shadow-md"></div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appointment Modal */}
        <AppointmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={handleAppointmentSubmitAndReload}
          appointment={selectedAppointment}
          mode={modalMode}
          prefilledDate={prefilledDate}
          prefilledTime={prefilledTime}
          services={services}
          onCustomerUpdated={handleCustomerUpdated}
        />

        {/* Constraint occurrence action picker */}
        {constraintActionPicker && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" dir="rtl">
            <div className="bg-white rounded-lg max-w-sm w-full p-6 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-1">עריכת הגבלה</h3>
              <p className="text-sm text-gray-500 mb-5">
                {constraintActionPicker.constraint.reason}
                {' · '}
                {new Date(constraintActionPicker.date + 'T12:00:00').toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'short' })}
              </p>
              <div className="flex flex-col gap-3">
                <button
                  className="w-full py-2.5 px-4 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-right"
                  onClick={() => {
                    const c = constraintActionPicker.constraint;
                    setConstraintActionPicker(null);
                    setSelectedConstraint(c);
                    setConstraintModalMode('edit');
                    setConstraintModalInitialData(null);
                    setIsConstraintModalOpen(true);
                  }}
                >
                  ערוך הגבלה גלובלית
                  <span className="block text-xs text-gray-400 font-normal">ישפיע על כל השבועות</span>
                </button>
                <button
                  className="w-full py-2.5 px-4 text-sm font-medium text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors text-right"
                  onClick={async () => {
                    const { constraint, date } = constraintActionPicker;
                    setConstraintActionPicker(null);
                    try {
                      await excludeDate(constraint.id, date);
                      setConstraintModalInitialData({
                        constraint_type: 'date_range',
                        start_date: date,
                        end_date: date,
                        start_time: constraint.start_time,
                        end_time: constraint.end_time,
                        crew_member_id: constraint.crew_member_id || '',
                        reason: constraint.reason
                      });
                      setSelectedConstraint(null);
                      setConstraintModalMode('add');
                      setIsConstraintModalOpen(true);
                    } catch {
                      // error already stored in hook state
                    }
                  }}
                >
                  שנה שעות לשבוע זה
                  <span className="block text-xs text-orange-400 font-normal">רק ליום {new Date(constraintActionPicker.date + 'T12:00:00').toLocaleDateString('he-IL', { weekday: 'long' })}</span>
                </button>
                <button
                  className="w-full py-2.5 px-4 text-sm font-medium text-red-700 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-right"
                  onClick={async () => {
                    const { constraint, date } = constraintActionPicker;
                    setConstraintActionPicker(null);
                    try {
                      await excludeDate(constraint.id, date);
                    } catch {
                      // error already stored in hook state
                    }
                  }}
                >
                  בטל הגבלה לשבוע זה
                  <span className="block text-xs text-red-400 font-normal">רק ליום {new Date(constraintActionPicker.date + 'T12:00:00').toLocaleDateString('he-IL', { weekday: 'long' })}</span>
                </button>
              </div>
              <button
                className="mt-4 w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                onClick={() => setConstraintActionPicker(null)}
              >
                ביטול
              </button>
            </div>
          </div>
        )}

        {/* Constraint Modal */}
        <ConstraintModal
          isOpen={isConstraintModalOpen}
          onClose={() => {
            setIsConstraintModalOpen(false);
            setConstraintModalInitialData(null);
          }}
          onSuccess={async (constraintData, mode) => {
            if (mode === 'add') {
              await createConstraint(constraintData);
            } else {
              await updateConstraint(selectedConstraint.id, constraintData);
            }
            await loadConstraints({ startDate: scheduleLoadStartDate, endDate: scheduleLoadEndDate });
          }}
          onDelete={async (constraintId) => {
            await deleteConstraint(constraintId);
            await loadConstraints({ startDate: scheduleLoadStartDate, endDate: scheduleLoadEndDate });
          }}
          constraint={selectedConstraint}
          mode={constraintModalMode}
          initialData={constraintModalInitialData}
        />

        {alertMessage && (
          <Alert
            message={alertMessage}
            variant={alertVariant}
            title={alertVariant === 'success' ? 'הושלם בהצלחה' : 'לתשומת ליבך'}
            onClose={() => setAlertMessage('')}
          />
        )}
        {/* Alert for appointment errors */}
        {appointmentsError && (
          <Alert
            message={appointmentsError}
            title="שגיאה"
            variant="error"
            onClose={clearAppointmentsError}
          />
        )}

        {/* Floating Add Button - Mobile Only */}
        {isMobile && !isModalOpen && (
          <button
            onClick={() => handleNewAppointment()}
            className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-gradient-to-br from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white rounded-full shadow-2xl drop-shadow-xl hover:shadow-pink-500/60 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 border-2 border-white ring-2 ring-pink-400/30"
            aria-label={t.newAppointment}
            title={t.newAppointment}
          >
            <Plus className="w-7 h-7 stroke-[3]" />
          </button>
        )}
      </PageContainer>
    </ContentLayout>
  );
}
